import { Component, h, Element, State, Prop, Watch, Listen} from "@stencil/core";
import { Filter, SearchDriver, SearchResult, helpers } from '@elastic/search-ui';
import AppSearchAPIConnector from '@elastic/search-ui-app-search-connector';
import i18nextInstance from "../../utils/i18n";
import { getAuthorNamesArray} from "../../utils/utils";
import MiniMasonry from "minimasonry";

/**
 *  Blog Overview Component. This component is used to display a list of blog articles.
 *  There is a searchbar to perform search requests and a filter area to filter the results.
 */
@Component({
  tag: "ad-blog-overview",
  styleUrl: "ad-blog-overview.css",
  shadow: true,
})

export class AdBlogOverview {

    searchbar: HTMLElement;
    observer: IntersectionObserver;
    masonry: MiniMasonry;
    searchUlList: HTMLElement;


    @Element() el: HTMLElement;
    @State() searchDriver: SearchDriver;
    @State() appSearchConnector: AppSearchAPIConnector;

    /** ElasticSearch Endpoint URL */
    @Prop({reflect:true, mutable: false}) esEndpoint: string;
    /** ElasticSearch Enginename */
    @Prop({reflect:true, mutable: false}) esEnginename: string;
    /** Searchterm / Query which is used to perform search request */
    @Prop({reflect:true, mutable: true}) searchterm: string = "";
    @Prop({reflect:true}) action: string;
    /** (optional) site filter */
    @Prop({reflect:true, mutable: true}) site?: string;

    @State() isLoading: boolean = false;
    @State() hasError: boolean = false;
    @State() isFilterVisible: boolean = false;

    // Pagination
    @State() currentPage: number = 1;
    @State() totalPages: number = 10;
    @State() totalResults: number = 0;
    @State() resultList: SearchResult[] = [];

    @State()
    searchFields = {
        title:{ weight: 10}, 
        headlines:{weight: 8}, 
        content:{},
        authors: {},
        author_multi_keyword: {}
    }

    @State() 
    resultFields = {
        id: { raw:{} },
        title:{ raw: {}, snippet:{fallback:true}},
        link:{ raw:{} },
        content_type_multi_keyword:{ raw:{} },
        teaser_picture: {raw:{} },
        "@timestamp": { raw:{} },
        category_keyword: { raw:{} },
        tag_id_multi_keyword: { raw:{} },
        authors: { raw:{} },
        author_multi_keyword: { raw:{} },
        teaser: { raw:{} },
        author_picture_stored_only: { raw:{} }
    }

    @State() disjunctiveFacets: string[] = ["category_keyword","tag_multi_keyword"];

    @State() 
    facetConf: Record<string, any> = {
        "category_keyword": {
            "type":"value",
            "name": "category_keyword",
            "sort": {"count":"desc"}
        },
        "tag_multi_keyword": {
            "type":"value",
            "name": "tag_multi_keyword",
            "sort": {"count":"desc"}
        }, 
    }

    @State() hideFilterInOutput = ["tag_multi_keyword"];

    @State() facetFilters: Record<string, any> = {};
    @State() filter: Filter[] = [{
        field: "content_type_multi_keyword", values: ["blog"],
        type: "any"
    }];

    @Watch('searchterm')
    onSearchtermChange(newValue: string, oldValue: string) {
        if(newValue !== oldValue) {
            this.searchDriver?.getActions().setSearchTerm(this.searchterm, {
                shouldClearFilters: true,
            });
            this.resetSelectboxes();
        }
    }

    @Listen('searchbarSubmit')
    onSearchbarSubmit(event: CustomEvent) {
        if(event.detail && event.detail !== this.searchterm) {
            this.searchterm = event.detail;
            this.searchDriver.setSort("@timestamp", "");
        }
    }

    @Listen('currentPageChanged')
    pageChangedEventhandler(event) {
        this.searchDriver.getActions().setCurrent(event.detail);
        this.searchbar.scrollIntoView({ behavior: "smooth", block:"center" });
    }

    @State() isTranslated : boolean = false;
    
    @Listen('translationsLoaded', {target: 'window'})
    translationsLoadedHandler() {
        this.isTranslated = true;
    }

    connectedCallback() {
		try {
			this.appSearchConnector = new AppSearchAPIConnector({
				endpointBase: this.esEndpoint,
				engineName: this.esEnginename,
			});
			this.searchDriver = new SearchDriver({
				apiConnector: this.appSearchConnector,
				hasA11yNotifications: true,
				a11yNotificationMessages: {
					searchResults: ({ start, end, totalResults }) =>
                        i18nextInstance.t("blognotification", {start, end, totalResults, ns:'fslabels'}),
				  },
				 initialState: {
				 	searchTerm: this.searchterm,
				 	resultsPerPage: 20,
				 	filters: this.filter
				 }
			});
            this.searchDriver.setSearchQuery({
                search_fields: this.searchFields,
                result_fields: this.resultFields,
                filters: this.filter,
                facets: this.facetConf,
                resultsPerPage: 20,
                disjunctiveFacets: this.disjunctiveFacets
            });

		} catch (err) {
			this.hasError = true;
		}

		this.searchDriver.subscribeToStateChanges((state) => {
           this.isLoading = state.isLoading;

			if(state.wasSearched) {
                
                this.currentPage = state.current;
                this.totalPages = state.totalPages;
                this.resultList = state.results;
                this.totalResults = state.totalResults;
                this.facetFilters = state.facets;
                this.hasError = false;
                
			} else if (state.error) {
                this.isLoading = false;
				this.hasError = true;
			}
		});

        this.observer = new IntersectionObserver(entries => {
			entries.forEach(entry => {
				entry.target.classList.toggle("show", entry.isIntersecting);
			});
		}, {
			rootMargin:"-50px"
		});

	}

    componentWillLoad() {
        const initialState = this.searchDriver.URLManager.getStateFromURL();
        this.searchterm = initialState.searchTerm || this.searchterm || "";
        this.searchDriver.setSort("@timestamp", "desc");
    }

    componentWillUpdate() {
        const elements = this.el.shadowRoot.querySelectorAll('article');
        elements.forEach(e => {
            this.observer.unobserve(e);
        });
    }

    componentDidRender() {
        const elements = this.el.shadowRoot.querySelectorAll('article');
        elements.forEach(e => {
            this.observer.observe(e);
        });
        this.masonry = new MiniMasonry({ 
			container:this.searchUlList,
			gutterX: 60,
			gutterY: 30,
			minify:false,
			wedge: true,
			surroundingGutter: false,
			baseWidth:350  
		});
    }

    componentDidUpdate() {
        this.masonry.layout();
    }

    onFilterToggleClickHandler() {
        this.isFilterVisible = !this.isFilterVisible;
    }

    onFilterClickResetHandler() {
        this.isFilterVisible = !this.isFilterVisible;
        this.searchterm = "";
        this.searchDriver.getActions().clearFilters();
        this.searchDriver.setSort("@timestamp", "desc");
        this.resetSelectboxes();
    }

    resetSelectboxes() {
        const selectBoxes = this.el.shadowRoot.querySelectorAll('#filtercontainer ad-select') as NodeListOf<HTMLAdSelectElement>;
        selectBoxes.forEach(selectBox => {
            selectBox.reset();
        });
    }

    onFilterChangeHandler(event) {
        const filterName = event.detail.name;
        const filterValue = event.detail.value;
        if(filterValue === "reset") {
            this.searchDriver.getActions().removeFilter(filterName);
        } else {
            this.searchDriver.getActions().setFilter(filterName, filterValue);
        }
    }

    render() {

        let filterContainer = (
            <div id="filtercontainer" inert={!this.isFilterVisible} class={this.isFilterVisible ? "active" : ""} aria-hidden={this.isFilterVisible} aria-label={i18nextInstance.t('filterarea')} role="menu">
                {Object.keys(this.facetFilters).map(facet => {
                    let facetData = this.facetFilters[facet][0];
                    if(!facetData) return;
                    if(this.hideFilterInOutput.includes(facet)) return;
                    const currentActiveFilter = helpers.markSelectedFacetValuesFromFilters(facetData,this.filter,facet,"any");
                    return (
                        <ad-select name={facet} key={facet} onSelectChange={this.onFilterChangeHandler.bind(this)}>
                            <option value="reset">{i18nextInstance.t(facetData.name)}</option>
                            {currentActiveFilter.data.map(filter => (
                                <option value={filter.value} selected={filter.selected}>{i18nextInstance.t(filter.value, { ns:"fslabels"})} ({filter.count})</option>
                            ))}
                        </ad-select>
                    );
                })}
                <div class="selectbox-container">
                    <ad-searchbar searchterm={this.searchterm} filter={this.filter} ref={searchbar => this.searchbar = searchbar} site={this.site} es-endpoint={this.esEndpoint} es-engineName={this.esEnginename} action={location.pathname}></ad-searchbar>
                </div>
             </div>
        );

        let searchResultListItems = (
            (this.resultList.length > 0 ? 
                <div id="searchResults" ref={ref => this.searchUlList = ref} aria-label={i18nextInstance.t('suggestionresult')}>
                    {this.resultList.map(result => (
                            <article class="blog-teaser" itemscope itemtype="http://schema.org/BlogPosting">
                                <header>
                                    <h2 itemprop="headline" class="heading"><a href={result.link?.raw} itemprop="url" data-result-id={result.id.raw}>{result.title.raw}</a></h2>
                                    <div class="flex">
                                        <div class="meta">
                                            <p class="category">{result.category_keyword?.raw}</p>
                                            <p class="date-author">
                                                <time 
                                                    dateTime={new Date(result["@timestamp"].raw).toISOString()} 
                                                    itemprop="datePublished" 
                                                    class="date">
                                                        {new Date(result["@timestamp"].raw).toLocaleDateString(i18nextInstance.language.toLocaleString(),{day:"2-digit",month:"2-digit",year:"numeric"})}
                                                </time>  
                                                    {result.authors? getAuthorNamesArray(result.authors?.raw)?.map(author => (<span itemprop="author" itemtype="http://schema.org/Person">{author}</span>)) : ""}
                                                        
                                            </p>
                                        </div>
                                        {result.author_picture_stored_only? 
                                            <figure class="author-picture">
                                                <img width="100" height="100" src={result.author_picture_stored_only?.raw} itemprop="image" loading="lazy" />
                                            </figure>
                                        : ''} 
                                    </div>
                                </header>
                                <p itemprop="description" class="teaser">{result.teaser.raw}</p>
                                <footer>
                                    <a href={result.link?.raw} itemprop="url" data-result-id={result.id.raw}>{i18nextInstance.t("blog.overview.read")}</a>
                                </footer>
                            </article>
                        
                    ))}
                </div>
                : <div id="searchResults" ref={ref => this.searchUlList = ref} aria-label={i18nextInstance.t('suggestionresult')}></div>
            )
        );

        return (
        <div id="blogContainer" role="search" class={this.isLoading ? "loading" : ""}>
            <div class="searchbar">
                
            </div>
            <div class="filter-meta">
            <button 
                    disabled={this.isLoading || this.hasError} 
                    onClick={this.onFilterClickResetHandler.bind(this)} 
                    type="button" 
                    aria-controls="filtercontainer" 
                    class="filtertoggle"
                >
                    <ad-icon icon={this.isFilterVisible ? "filter-slash fa-xl" : "filter-list fa-xl"}></ad-icon> 
                    <span>{i18nextInstance.t(`${this.isFilterVisible ? "filter_reset" : "filter_show"}`)}</span>
                </button> 
                <div class="meta-info" innerHTML={i18nextInstance.t('blog_result', {count:this.totalResults, term:this.searchterm})}></div>
            </div>
            {filterContainer}
            <div id="loader"></div>
            {this.hasError && <ad-error></ad-error>}
            <div class="flex r-reverse result-filter-container" aria-live="polite">
                <div id="resultContainer">
                    {searchResultListItems}
                </div>
            </div>
            <ad-pagination disabled={this.isLoading || this.hasError} current-page={this.currentPage} total-pages={this.totalPages}></ad-pagination>
        </div>
        )
    }
}


